import React from "react";
import { Controller, useFieldArray, useFormContext, useWatch } from "react-hook-form";
import { intl } from "LocaleWrapper";

import { Button, Card, FormGroup, Select } from "@cofano/cck";
import { Box, Grid2 as Grid } from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";

import { FormErrorMessage } from "components/form/FormErrorMessage";

import { Language } from "imports/Utils";

import LocationSelect from "view/locations/components/selects/LocationSelect";
import { RelationSchemaInput, RelationSchemaOutput } from "view/relations/utils/relation.schema";

import { LocationUserType } from "__generated__/enums";

const RelationLocationsForm = () => {

	const { control } = useFormContext<RelationSchemaInput, unknown, RelationSchemaOutput>();

	const { fields, append, remove } = useFieldArray({
		control,
		name: "locations",
	});

	const [ locations, isLocationManager ] = useWatch({ control, name: [ "locations", "isLocationRelation" ] });

	const defaultLocation = {
		id: null,
		location: null,
		type: LocationUserType.Owner,
	};

	const options = [ LocationUserType.Owner ];

	if (!isLocationManager) {
		options.push(LocationUserType.User);
	}

	return (
		<Box p={2} pt={fields.length === 0 ? 2 : 0}>
			{fields.map((item, index) => {
				return (
					<Card
						key={item.id}
						title={Language("LOCATION")}
						subtitle={locations?.[index]?.location?.description}
						actions={[ {
							onClick: () => remove(index),
							icon: <DeleteIcon />,
						} ]}
						collapsable>
						<Grid spacing={3} container>
							<Grid size={{ xs: 12, sm: 6 }}>
								<FormGroup label={Language("LOCATION")} overflow="hidden" required>
									<Controller
										name={`locations.${index}.location`}
										control={control}
										render={({ field, formState }) => (
											<React.Fragment>
												<LocationSelect {...field} />
												<FormErrorMessage name={field.name} errors={formState.errors} />
											</React.Fragment>
										)} />
								</FormGroup>
							</Grid>

							<Grid size={{ xs: 12, sm: 6 }}>
								<FormGroup label={Language("TYPE")} overflow="hidden">
									<Controller
										name={`locations.${index}.type`}
										control={control}
										render={({ field }) => (
											<Select
												{...field}
												fetchOptions={options}
												getOptionLabel={option => intl.formatMessage({ id: `LOCATION_${option}` })}
												clearable={false}
												disableSearch />
										)} />
								</FormGroup>
							</Grid>
						</Grid>
					</Card>
				);
			})}

			<Box>
				<Button
					icon={<AddIcon />}
					onClick={() => append(defaultLocation)}
					size="small"
					variant="outlined" />
			</Box>
		</Box>
	);
};

export default RelationLocationsForm;
